import { useMemo } from 'react';
import { signUpPath } from 'common/config/urls';
import {
  AUTH_LOADING_LABELS,
  AUTH_SOURCE_SECURE_PORTAL,
  COMPANY_VALUATIONS,
  PORTFOLIO_VALUATION,
  SIGN_IN_PRODUCT,
  SIGN_IN_VIEW,
  type SignInProduct,
} from 'common/constants/auth';
import {
  areSignInAuthRequirementsPending,
  getSignInLoadingLabel,
  getSignInProductSelectionOptions,
  signInViewRequiresSession,
} from 'pages/Auth/SignIn/SignIn.utils';
import {
  buildAuthSearchParams,
  getAppAutoSelect,
  getProductAccess,
  normalizeSignInRouteState,
  sanitizeReturnTo,
  type SignInRouteState,
} from 'pages/Auth/utils';
import type { UseSignInViewModelParams } from './useSignInViewModel.types';

const useSignInViewModel = ({
  accessToken,
  authRequirements,
  currentUser,
  mode,
  product,
  reason,
  returnTo,
  source,
  view,
}: UseSignInViewModelParams) => {
  const safeReturnTo = useMemo(() => sanitizeReturnTo(returnTo), [returnTo]);
  const isSecurePortalSource = source === AUTH_SOURCE_SECURE_PORTAL;

  const productAccess = useMemo(
    () => (currentUser ? getProductAccess(currentUser, authRequirements.firmList.length) : null),
    [authRequirements.firmList.length, currentUser]
  );

  const resolvedProduct = useMemo<SignInProduct | null>(() => {
    if (product) {
      return product;
    }

    if (!productAccess) {
      return null;
    }

    const autoApp = getAppAutoSelect();
    // Explicit app-switch intents should bypass the generic chooser, even for users who can access both products.
    if (autoApp === PORTFOLIO_VALUATION && productAccess.hasPortfolioAccess) {
      return SIGN_IN_PRODUCT.PORTFOLIO;
    }
    if (autoApp === COMPANY_VALUATIONS && productAccess.hasCompanyAccess) {
      return SIGN_IN_PRODUCT.COMPANY;
    }

    // Without an explicit switch intent, users who can access both products should still choose deliberately.
    if (productAccess.hasBothProducts) {
      return null;
    }

    if (productAccess.hasCompanyAccess) {
      return SIGN_IN_PRODUCT.COMPANY;
    }

    if (productAccess.hasPortfolioAccess) {
      return SIGN_IN_PRODUCT.PORTFOLIO;
    }

    return null;
  }, [product, productAccess]);

  const productSelectionOptions = useMemo(() => getSignInProductSelectionOptions(productAccess), [productAccess]);

  const normalizedRouteState = useMemo<SignInRouteState>(
    () =>
      /*
      The sign-in flow stores state in the URL (view, mode, product, reason, returnTo, source).
      As the user moves through steps, old params can linger in the URL even though they no
      longer apply. Normalization cleans that up. The function normalizeSignInRouteState defines,
      for each view, which params should exist and which should be cleared.
      */
      normalizeSignInRouteState({
        mode,
        product,
        reason,
        returnTo,
        source,
        view,
      }),
    [mode, product, reason, returnTo, source, view]
  );

  const areRequirementsPending = useMemo(
    () =>
      areSignInAuthRequirementsPending({
        firmListStatus: authRequirements.firmListStatus,
        hasCurrentUser: Boolean(currentUser),
        isLoading: authRequirements.isLoading,
      }),
    [authRequirements.firmListStatus, authRequirements.isLoading, currentUser]
  );

  const isResolvingPostAuthView = useMemo(
    // Prevent rendering the wrong post-auth step while user info and auth requirements are still loading.
    () => signInViewRequiresSession(view) && Boolean(accessToken) && (!currentUser || areRequirementsPending),
    [accessToken, areRequirementsPending, currentUser, view]
  );

  const shouldShowFlowLoading = isResolvingPostAuthView;

  const showSignUpFooter = useMemo(
    () =>
      !shouldShowFlowLoading
      && (view === SIGN_IN_VIEW.IDENTIFIER || view === SIGN_IN_VIEW.PASSWORD),
    [shouldShowFlowLoading, view]
  );

  const showFooterSkip = useMemo(
    () =>
      isSecurePortalSource
      && (view === SIGN_IN_VIEW.IDENTIFIER || view === SIGN_IN_VIEW.PASSWORD),
    [isSecurePortalSource, view]
  );

  const signUpHref = useMemo(() => {
    const params = buildAuthSearchParams({
      returnTo: safeReturnTo,
      source,
    });
    const query = params.toString();

    return query ? `${signUpPath}?${query}` : signUpPath;
  }, [safeReturnTo, source]);

  const loadingLabel = useMemo(
    () => (isResolvingPostAuthView && !currentUser
      ? AUTH_LOADING_LABELS.session
      : getSignInLoadingLabel(view)),
    [currentUser, isResolvingPostAuthView, view]
  );

  return {
    loadingLabel,
    normalizedRouteState,
    productAccess,
    productSelectionOptions,
    resolvedProduct,
    safeReturnTo,
    showFooterSkip,
    showSignUpFooter,
    signUpHref,
    shouldShowFlowLoading,
  };
};

export default useSignInViewModel;
